// src/pages/Admin/AddPortfolioModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Heading,
    Text,
    TextInput,
    Select,
    Spinner,
    Notification,
    Layer,
} from "grommet";
import axios from "axios";
import API_URL from "../../config/config";
import { useNavigate } from "react-router-dom";

const AddPortfolioModal = ({ onClose, onNext }) => {
    const [portfolioName, setPortfolioName] = useState("");

    const [courses, setCourses] = useState([]);
    const [learningPaths, setLearningPaths] = useState([]);
    const [skills, setSkills] = useState([]);

    const [selectedCourses, setSelectedCourses] = useState([]); // course_ids
    const [selectedPaths, setSelectedPaths] = useState([]);     // learningpath_ids
    const [selectedSkills, setSelectedSkills] = useState([]);   // skill_ids

    // Global mapping (persisted when user clicks "Save" in mapping modal)
    const [courseSkillMap, setCourseSkillMap] = useState({});   // { [courseId]: skillId }

    const [loading, setLoading] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const [toastStatus, setToastStatus] = useState("normal");
    const [showToast, setShowToast] = useState(false);

    const [showConfirm, setShowConfirm] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);    // <— NEW sub-modal state

    // Temp map used inside the mapping modal (so Cancel doesn’t overwrite main state)
    const [tempCourseSkillMap, setTempCourseSkillMap] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await axios.get(`${API_URL}/admin/all-details`);
                const data = res.data;

                const uniqueCourses = Object.values(
                    (data.courses || []).reduce((acc, course) => {
                        acc[course.course_id] = course;
                        return acc;
                    }, {})
                );

                setCourses(uniqueCourses);
                setLearningPaths(data.learningPaths || []);
                setSkills(data.skills || []);
            } catch (error) {
                console.error("Error loading portfolio options:", error);
            }
        };

        fetchDetails();
    }, []);

    // --- helpers / options ---
    const canPickCourses = selectedSkills.length > 0;

    const skillOptions = useMemo(
        () =>
            selectedSkills.map((sid) => {
                const s = skills.find((x) => x.skill_id === sid);
                return { label: s?.skill_name ?? String(sid), value: sid };
            }),
        [selectedSkills, skills]
    );

    const getLabelsByIds = (selectedIds, items, idKey, labelKey) =>
        items.filter((item) => selectedIds.includes(item[idKey])).map((item) => item[labelKey]);

    const courseLabel = (cid) => {
        const c = courses.find((x) => x.course_id === cid);
        return c ? `${c.course_code} - ${c.course_name}` : String(cid);
    };

    // Keep main mapping clean if user removes a course
    useEffect(() => {
        setCourseSkillMap((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((k) => {
                const cid = Number.isNaN(+k) ? k : +k;
                if (!selectedCourses.includes(cid)) delete next[k];
            });
            return next;
        });
    }, [selectedCourses]);

    // Keep mapping valid if user removes a selected skill that was used in a map
    useEffect(() => {
        setCourseSkillMap((prev) => {
            const next = { ...prev };
            let changed = false;
            Object.keys(next).forEach((k) => {
                const sid = next[k];
                if (sid && !selectedSkills.includes(sid)) {
                    next[k] = undefined;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [selectedSkills]);

    // Validation: all selected courses must have a skill map
    const allCoursesMapped =
        selectedCourses.length === 0 ||
        selectedCourses.every((cid) => !!courseSkillMap[String(cid)] || !!courseSkillMap[cid]);

    const disabledNext =
        loading ||
        !portfolioName.trim() ||
        (selectedCourses.length > 0 && (!selectedSkills.length || !allCoursesMapped));

    // --- Map Modal open/close ---
    const openMapModal = () => {
        // Seed temp map with current persisted map, but only for currently selected courses
        const seed = {};
        selectedCourses.forEach((cid) => {
            seed[String(cid)] = courseSkillMap[String(cid)] ?? courseSkillMap[cid] ?? "";
        });
        setTempCourseSkillMap(seed);
        setShowMapModal(true);
    };

    const closeMapModal = () => {
        setShowMapModal(false);
    };

    const saveMapModal = () => {
        // Ensure every selected course has a selection
        const incomplete = selectedCourses.some((cid) => !tempCourseSkillMap[String(cid)]);
        if (incomplete) {
            setToastStatus("critical");
            setToastMessage("Please select a skill for every chosen course.");
            setShowToast(true);
            return;
        }
        setCourseSkillMap({ ...tempCourseSkillMap });
        setShowMapModal(false);
    };

    // --- Confirm / Submit ---
    const handleNext = () => {
        if (!portfolioName.trim()) {
            setToastStatus("critical");
            setToastMessage("Please enter a portfolio name.");
            setShowToast(true);
            return;
        }
        if (selectedCourses.length > 0 && selectedSkills.length === 0) {
            setToastStatus("critical");
            setToastMessage("Select at least one skill to enable courses.");
            setShowToast(true);
            return;
        }
        if (!allCoursesMapped) {
            setToastStatus("critical");
            setToastMessage("Please click 'Map Course Skills' and map each selected course.");
            setShowToast(true);
            return;
        }
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        const courseSkillPairs = selectedCourses.map((cid) => ({
            course_id: cid,
            skill_id: courseSkillMap[String(cid)] ?? courseSkillMap[cid] ?? null,
        }));

        const portfolioData = {
            portfolio_name: portfolioName.trim(),
            course_ids: selectedCourses,
            path_ids: selectedPaths,
            skill_ids: selectedSkills,
            course_skill_map: courseSkillPairs, // detailed mapping
        };

        try {
            setLoading(true);
            const response = await fetch("/api/admin/create-portfolio", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(portfolioData),
            });

            if (!response.ok) throw new Error("Failed to create portfolio");

            const { portfolio_id } = await response.json();
            setToastStatus("normal");
            setToastMessage("Portfolio created successfully!");
            setShowToast(true);
            setTimeout(() => {
                navigate(`/admin/portfolio/${portfolio_id}`);
            }, 1500);
        } catch (error) {
            console.error(error);
            setToastStatus("critical");
            setToastMessage("Failed to create portfolio. Please try again.");
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    // Count how many courses are currently mapped (for mini-status in main modal)
    const mappedCount = selectedCourses.filter(
        (cid) => !!courseSkillMap[String(cid)] || !!courseSkillMap[cid]
    ).length;

    return (
        <Layer onEsc={onClose} onClickOutside={onClose} responsive>
            <Box pad="medium" gap="medium" width="large">
                <Heading level="3" margin="none">
                    Add New Portfolio
                </Heading>

                {/* Portfolio Name */}
                <Box>
                    <Text margin={{ bottom: "xsmall" }}>Portfolio Name *</Text>
                    <TextInput
                        placeholder="Enter portfolio name"
                        value={portfolioName}
                        onChange={(e) => setPortfolioName(e.target.value)}
                    />
                </Box>

                {/* Skills */}
                <Box>
                    <Text margin={{ bottom: "xsmall" }}>Add Skill(s)</Text>
                    <Select
                        multiple
                        options={skills.map((s) => ({ label: s.skill_name, value: s.skill_id }))}
                        labelKey="label"
                        valueKey={{ key: "value", reduce: true }}
                        value={selectedSkills}
                        onChange={({ value }) => setSelectedSkills(value)}
                        placeholder="Select skills"
                        dropHeight="185px"
                    />
                    {selectedSkills.length > 0 && (
                        <Box pad={{ vertical: "xsmall" }}>
                            <Text size="small" color="dark-4">
                                • {getLabelsByIds(selectedSkills, skills, "skill_id", "skill_name").join(", ")}
                            </Text>
                        </Box>
                    )}
                </Box>

                {/* Courses */}
                <Box>
                    <Text margin={{ bottom: "xsmall" }}>Add Course(s)</Text>
                    <Select
                        multiple
                        disabled={selectedSkills.length === 0}
                        options={courses.map((c) => ({
                            label: `${c.course_code} - ${c.course_name}`,
                            value: c.course_id,
                        }))}
                        labelKey="label"
                        valueKey={{ key: "value", reduce: true }}
                        value={selectedCourses}
                        onChange={({ value }) => setSelectedCourses(value)}
                        placeholder={selectedSkills.length ? "Select courses" : "Select skills first"}
                        dropHeight="185px"
                    />
                    {selectedCourses.length > 0 && (
                        <Box direction="row" align="center" gap="small" margin={{ top: "xsmall" }}>
                            <Text size="small" color="dark-4">
                                • {getLabelsByIds(selectedCourses, courses, "course_id", "course_name").join(", ")}
                            </Text>
                        </Box>
                    )}

                    {/* Open mapping sub-modal */}
                    <Box direction="row" align="center" gap="small" margin={{ top: "small" }}>
                        <Button
                            secondary
                            label={`Map Course Skills${selectedCourses.length ? ` (${mappedCount}/${selectedCourses.length})` : ""}`}
                            onClick={openMapModal}
                            disabled={selectedSkills.length === 0 || selectedCourses.length === 0}
                        />
                        {selectedCourses.length > 0 && !allCoursesMapped && (
                            <Text size="small" color="status-warning">
                                Mapping required for all selected courses.
                            </Text>
                        )}
                    </Box>
                </Box>

                {/* Learning Paths */}
                <Box>
                    <Text margin={{ bottom: "xsmall" }}>Add Learning Path(s)</Text>
                    <Select
                        multiple
                        options={learningPaths.map((p) => ({
                            label: p.learningpath_name,
                            value: p.learningpath_id,
                        }))}
                        labelKey="label"
                        valueKey={{ key: "value", reduce: true }}
                        value={selectedPaths}
                        onChange={({ value }) => setSelectedPaths(value)}
                        placeholder="Select learning paths"
                        dropHeight="185px"
                    />
                    {selectedPaths.length > 0 && (
                        <Box pad={{ vertical: "xsmall" }}>
                            <Text size="small" color="dark-4">
                                • {getLabelsByIds(
                                    selectedPaths,
                                    learningPaths,
                                    "learningpath_id",
                                    "learningpath_name"
                                ).join(", ")}
                            </Text>
                        </Box>
                    )}
                </Box>

                <Text size="small" color="dark-5">
                    You can add questions by going to the <strong>Skill Question Mapping</strong> page later.
                </Text>

                {/* Actions */}
                <Box direction="row" justify="end" gap="medium">
                    <Button label="Cancel" onClick={onClose} />
                    <Button
                        primary
                        label={loading ? <Spinner size="xsmall" /> : "Next"}
                        onClick={handleNext}
                        disabled={disabledNext}
                    />
                </Box>

                {/* Confirm Layer */}
                {showConfirm && (
                    <Layer onEsc={() => setShowConfirm(false)} onClickOutside={() => setShowConfirm(false)}>
                        <Box pad="medium" gap="small" width="medium">
                            <Heading level="3" margin="none">
                                Confirm Portfolio Details
                            </Heading>

                            <Text>
                                <strong>New Portfolio Name:</strong> {portfolioName}
                            </Text>

                            <Box>
                                <Text weight="bold">Skills:</Text>
                                {getLabelsByIds(selectedSkills, skills, "skill_id", "skill_name").map((name, i) => (
                                    <Text key={i} size="small">
                                        • {name}
                                    </Text>
                                ))}
                            </Box>

                            <Box>
                                <Text weight="bold">Courses (with mapped skill):</Text>
                                {selectedCourses.map((cid) => {
                                    const cName =
                                        courses.find((c) => c.course_id === cid)?.course_name ?? String(cid);
                                    const sid = courseSkillMap[String(cid)] ?? courseSkillMap[cid];
                                    const sName =
                                        skills.find((s) => s.skill_id === sid)?.skill_name ?? "(not mapped)";
                                    return (
                                        <Text key={String(cid)} size="small">
                                            • {cName} — <em>{sName}</em>
                                        </Text>
                                    );
                                })}
                            </Box>

                            <Box>
                                <Text weight="bold">Learning Paths:</Text>
                                {getLabelsByIds(
                                    selectedPaths,
                                    learningPaths,
                                    "learningpath_id",
                                    "learningpath_name"
                                ).map((name, i) => (
                                    <Text key={i} size="small">
                                        • {name}
                                    </Text>
                                ))}
                            </Box>

                            <Box direction="row" justify="end" gap="medium" margin={{ top: "small" }}>
                                <Button label="Back" onClick={() => setShowConfirm(false)} />
                                <Button primary label="Confirm" onClick={handleConfirm} />
                            </Box>
                        </Box>
                    </Layer>
                )}

                {/* Mapping Sub-Modal */}
                {showMapModal && (
                    <Layer onEsc={closeMapModal} onClickOutside={closeMapModal} responsive>
                        <Box pad="medium" gap="small" width="large">
                            <Heading level="3" margin="none">
                                Map Course Skills
                            </Heading>
                            <Text size="small" color="dark-5">
                                Choose a skill (from your selected skills) for each course.
                            </Text>

                            {selectedCourses.length === 0 ? (
                                <Text color="status-warning" margin={{ top: "small" }}>
                                    Please select at least one course first.
                                </Text>
                            ) : selectedSkills.length === 0 ? (
                                <Text color="status-warning" margin={{ top: "small" }}>
                                    Please select skills first to enable mapping.
                                </Text>
                            ) : (
                                <Box gap="xsmall" margin={{ top: "xsmall" }}>
                                    {selectedCourses.map((cid) => (
                                        <Box key={String(cid)} direction="row" align="center" gap="small">
                                            <Box flex>
                                                <Text size="small">{courseLabel(cid)}</Text>
                                            </Box>
                                            <Box width="medium">
                                                <Select
                                                    options={skillOptions}
                                                    labelKey="label"
                                                    valueKey={{ key: "value", reduce: true }}
                                                    placeholder="Select a skill"
                                                    value={tempCourseSkillMap[String(cid)] ?? ""}
                                                    onChange={({ value }) =>
                                                        setTempCourseSkillMap((prev) => ({
                                                            ...prev,
                                                            [String(cid)]: value,
                                                        }))
                                                    }
                                                    dropHeight="185px"
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Box direction="row" justify="end" gap="small" margin={{ top: "small" }}>
                                <Button label="Cancel" onClick={closeMapModal} />
                                <Button
                                    primary
                                    label="Save"
                                    onClick={saveMapModal}
                                    disabled={
                                        selectedCourses.length === 0 ||
                                        selectedSkills.length === 0 ||
                                        selectedCourses.some((cid) => !tempCourseSkillMap[String(cid)])
                                    }
                                />
                            </Box>
                        </Box>
                    </Layer>
                )}

                {showToast && (
                    <Notification
                        toast
                        status={toastStatus}
                        title={toastStatus === "normal" ? "Success" : "Error"}
                        message={toastMessage}
                        onClose={() => setShowToast(false)}
                    />
                )}
            </Box>
        </Layer>
    );
};

export default AddPortfolioModal;
